import { badgeToneClass } from "components/ui/StatusBadge/StatusBadge";
import {
  BULK_RESEND_SUBJECT_MAX_LENGTH,
  type BulkResendPreviewItem,
} from "hooks/use-delivery-series-api";
import { cn } from "lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { type FC, useId, useState } from "react";
import { formatShortDateTime } from "utils/relative-time";
import {
  type BulkResendDraft,
  eligibilityReason,
  isChanged,
  TEMPLATE_UNAVAILABLE_MESSAGE,
} from "./bulk-resend-logic";

/** 空白だけのときのエラー文 (DeliveryForm の deliveryFormSchema と同じ文言) */
const SUBJECT_REQUIRED_MESSAGE = "件名を入力してください";
const BODY_REQUIRED_MESSAGE = "本文を入力してください";

const UNSENDABLE_PREFIX = "送信できません：";
const EDITED_LABEL = "編集済み";
const EDIT_TOGGLE_LABEL = "内容を確認・編集";
const EMPTY_SUBJECT_PLACEHOLDER = "（件名なし）";
const LABEL_SENT_AT = "送信内容";
const LABEL_SENDER = "送信者";
const LABEL_GROUP = "配信先";
const LABEL_INCLUDE_SENDER = "自分にも送信";
const INCLUDE_SENDER_YES = "する";
const INCLUDE_SENDER_NO = "しない";

/**
 * 「送信内容」の値. 前回の配信をコピーして送ることが分かる表現にする.
 * @param {string} sentAt 前回の配信の送信日時 (表示用に整形済み)
 * @return {string} 表示する値
 */
const sentAtValue = (sentAt: string): string =>
  `前回の配信（${sentAt}）をコピー`;

export type BulkResendSeriesItemProps = {
  item: BulkResendPreviewItem;
  draft: BulkResendDraft | undefined;
  /** 実行中など、入力を受け付けないとき true */
  readOnly: boolean;
  onChange: (next: BulkResendDraft) => void;
};

/**
 * 一括再配信の確認モーダルのシリーズ 1 件.
 * 送れるシリーズは折りたたみ時に名前だけを出し、開くと件名・項目表・編集欄を出す.
 * 送れないシリーズは名前と理由を出す.
 * @param {BulkResendSeriesItemProps} props シリーズと編集値
 * @return {JSX.Element} 1 件分
 */
export const BulkResendSeriesItem: FC<BulkResendSeriesItemProps> = ({
  item,
  draft,
  readOnly,
  onChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const editorId = useId();
  const name = item.seriesName ?? "—";
  const reason = eligibilityReason(item.eligibility);
  const { template, recipients } = item;

  if (reason !== null || !template || !draft) {
    return (
      <li className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
        <span className="text-xs font-bold text-slate-400">{name}</span>
        <span className="text-xs text-base-content/60">
          {UNSENDABLE_PREFIX}
          {reason ?? TEMPLATE_UNAVAILABLE_MESSAGE}
        </span>
      </li>
    );
  }

  const edited =
    isChanged(draft.subject, template.subject) ||
    isChanged(draft.bodyText, template.bodyText);
  const subjectBlank = draft.subject.trim() === "";
  const bodyBlank = draft.bodyText.trim() === "";
  // 配信先のグループ名の下の行に薄く添える件数 (「宛先数」のラベルは付けない)
  const counts = recipients
    ? `${recipients.activeContactCount}件（${recipients.companyCount}社）${
        recipients.optedOutCount > 0
          ? ` · 受信拒否 ${recipients.optedOutCount}件`
          : ""
      }`
    : "";

  return (
    <li className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      {/* tailwind.config の fontSize は行の高さを持たないので、text-xs は preflight の line-height 1.5 で 18px。
          名前と開閉ボタンは 18px、「編集済み」バッジ (sm) は 18 + py-0.5 の 4 + 枠線 2 = 24px。
          バッジの有無で高さが変わらないよう、最初から 24px を確保する */}
      <div className="flex min-h-6 flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-700">{name}</span>
        {edited && (
          <span className={badgeToneClass["soft-info"]}>{EDITED_LABEL}</span>
        )}
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary"
          aria-expanded={expanded}
          aria-controls={editorId}
          disabled={readOnly}
          onClick={() => setExpanded(prev => !prev)}
        >
          {expanded ? (
            <ChevronDown size={12} aria-hidden />
          ) : (
            <ChevronRight size={12} aria-hidden />
          )}
          {EDIT_TOGGLE_LABEL}
        </button>
      </div>
      {/* 閉じていても、送信ボタンが押せない理由が分かるようにする */}
      {!expanded && (subjectBlank || bodyBlank) && (
        <div>
          {subjectBlank && (
            <p className="text-xs text-error">{SUBJECT_REQUIRED_MESSAGE}</p>
          )}
          {bodyBlank && (
            <p className="text-xs text-error">{BODY_REQUIRED_MESSAGE}</p>
          )}
        </div>
      )}
      {expanded && (
        <div id={editorId}>
          {subjectBlank ? (
            <p className="text-sm text-slate-400">
              {EMPTY_SUBJECT_PLACEHOLDER}
            </p>
          ) : (
            <p className="break-all text-sm font-semibold text-base-content">
              {draft.subject}
            </p>
          )}
          {/* 2 列 × 2 行 (左: 送信内容・配信先 / 右: 送信者・自分にも送信)。狭い幅では 1 列 */}
          <dl className="mt-1 grid grid-cols-[auto_1fr] items-baseline gap-x-2.5 gap-y-px text-xs sm:grid-cols-[auto_1fr_auto_1fr]">
            <dt className="whitespace-nowrap text-slate-400">
              {LABEL_SENT_AT}
            </dt>
            <dd className="min-w-0 break-words text-slate-700">
              {sentAtValue(formatShortDateTime(template.sentAt))}
            </dd>
            <dt className="whitespace-nowrap text-slate-400">{LABEL_SENDER}</dt>
            <dd className="min-w-0 break-all text-slate-700">
              {template.fromName ? (
                <>
                  {template.fromName}{" "}
                  <span className="text-slate-500">({template.fromEmail})</span>
                </>
              ) : (
                template.fromEmail
              )}
            </dd>
            <dt className="whitespace-nowrap text-slate-400">{LABEL_GROUP}</dt>
            <dd className="min-w-0 break-words text-slate-700">
              <span className="block">{template.groupName ?? "—"}</span>
              {counts && (
                <span className="block text-xs text-slate-500">{counts}</span>
              )}
            </dd>
            <dt className="whitespace-nowrap text-slate-400">
              {LABEL_INCLUDE_SENDER}
            </dt>
            <dd className="text-slate-700">
              {template.includeSender ? INCLUDE_SENDER_YES : INCLUDE_SENDER_NO}
            </dd>
          </dl>
          <div className="mt-2 space-y-1 border-t border-dashed border-slate-200 pt-1.5">
            <label className="block text-xs text-base-content/70">
              件名 <span className="text-error">*</span>
              <input
                type="text"
                className={cn(
                  "input input-bordered input-sm mt-1 w-full",
                  subjectBlank && "input-error",
                )}
                value={draft.subject}
                maxLength={BULK_RESEND_SUBJECT_MAX_LENGTH}
                disabled={readOnly}
                onChange={e => onChange({ ...draft, subject: e.target.value })}
              />
            </label>
            {subjectBlank && (
              <p className="text-xs text-error">{SUBJECT_REQUIRED_MESSAGE}</p>
            )}
            <label className="block text-xs text-base-content/70">
              本文 <span className="text-error">*</span>
              <textarea
                className={cn(
                  "textarea textarea-bordered mt-1 w-full text-sm",
                  bodyBlank && "textarea-error",
                )}
                rows={8}
                value={draft.bodyText}
                disabled={readOnly}
                onChange={e => onChange({ ...draft, bodyText: e.target.value })}
              />
            </label>
            {bodyBlank && (
              <p className="text-xs text-error">{BODY_REQUIRED_MESSAGE}</p>
            )}
            <p className="text-xs text-base-content/60">
              変数：{"{{COMPANY}}"} = 会社名, {"{{NAME}}"} = 担当者名
            </p>
          </div>
        </div>
      )}
    </li>
  );
};
